import * as AccordionPrimitive from "@radix-ui/react-accordion";
import * as React from "react";
import { FaChevronDown } from "react-icons/fa";
import { cn } from "~/lib/utils";
import { ICON_ORANGE_COLOR } from "~/utils/constant";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => <AccordionPrimitive.Item ref={ref} className={cn("", className)} {...props} />);

AccordionItem.displayName = "AccordionItem";

type AccordionTriggerProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
  iconVisible?: boolean;
  closeChildren?: React.ReactNode;
  darkBackground?: boolean;
};
const AccordionTrigger = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Trigger>, AccordionTriggerProps>(
  ({ className, children, iconVisible, closeChildren, darkBackground, ...props }, ref) => (
    <div className="flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn("group flex flex-1 items-center justify-between text-sm font-medium transition-all text-left", className)}
        {...props}
      >
        {children}
        <div className="flex gap-x-2 items-center">
          {iconVisible && (
            <div
              className={cn(
                "size-[30px] bg-white rounded-full flex justify-center items-center cursor-pointer transition-transform duration-300 group-data-[state=open]:rotate-180",
                darkBackground && `bg-darkBackground`,
              )}
            >
              {closeChildren ? (
                <MdOutlineKeyboardArrowDown size={25} color={ICON_ORANGE_COLOR} className="shrink-0 text-muted-foreground" />
              ) : (
                <FaChevronDown size={15} color={ICON_ORANGE_COLOR} className="shrink-0 text-muted-foreground" />
              )}
            </div>
          )}

          {closeChildren && <div>{closeChildren}</div>}
        </div>
      </AccordionPrimitive.Trigger>
    </div>
  ),
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
